(() => {
  const readyEventName = 'voidscreen:collection-carousel-ready';

  if (window.VoidScreenCollectionCarousel?.version === 'seam-18-single-cover') {
    document.dispatchEvent(new CustomEvent(readyEventName));
    return;
  }

  const CAROUSEL_STEP_MS = 1200;
  const CAROUSEL_STEPS = 4;
  const OPEN_GRID_MS = 1250;
  const GRID_HOLD_MS = 1900;
  const CLOSE_GRID_MS = 850;
  const BASE_SEQUENCE = ['cover', 'gift', 'mystery', 'mystery'];
  const LIBRARY_SEQUENCE = [
    'cover',
    'gift',
    'mystery',
    'mystery',
    'mystery',
    'gift',
    'mystery',
    'mystery',
    'mystery',
  ];
  const slotClasses = ['is-far-left', 'is-left', 'is-center', 'is-right', 'is-far-right'];
  const modeClasses = ['is-cover-press', 'is-opening-grid', 'is-grid-view', 'is-grid-press', 'is-closing-grid'];
  const instances = [];

  const escapeAttribute = (value) =>
    String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#039;',
      '"': '&quot;',
    })[character]);

  const checkIcon = `
    <svg class="check-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5.5 12.5 4 4 9-10" />
    </svg>`;

  const giftIcon = `
    <svg class="gift-icon" viewBox="0 0 48 48" aria-hidden="true">
      <path d="M8 20h32v21H8zM5 14.5h38V21H5zM24 14.5V41" />
      <path d="M23.5 14.5c-6.7.3-11.7-1.4-11.7-5.1 0-2.1 1.7-3.8 4-3.8 4.2 0 7.7 8.9 7.7 8.9ZM24.5 14.5c6.7.3 11.7-1.4 11.7-5.1 0-2.1-1.7-3.8-4-3.8-4.2 0-7.7 8.9-7.7 8.9Z" />
    </svg>`;

  function carouselCardMarkup(type, coverUrl) {
    if (type === 'cover') {
      return `
        <div class="carousel-card carousel-card--cover" data-card-type="cover">
          <img src="${escapeAttribute(coverUrl)}" alt="" />
          <span class="status-check">${checkIcon}</span>
        </div>`;
    }

    if (type === 'gift') {
      return `
        <div class="carousel-card carousel-card--gift" data-card-type="gift">
          ${giftIcon}
          <span class="status-check">${checkIcon}</span>
        </div>`;
    }

    return `
      <div class="carousel-card carousel-card--mystery" data-card-type="mystery" aria-hidden="true">
        <span>?</span>
      </div>`;
  }

  function libraryCardMarkup(type, coverUrl, index) {
    if (type === 'cover') {
      return `
        <div class="library-card library-card--cover" data-library-position="${index + 1}" aria-hidden="true"></div>`;
    }

    if (type === 'gift') {
      return `
        <div class="library-card library-card--gift" data-library-position="${index + 1}">
          ${giftIcon}
        </div>`;
    }

    return `
      <div class="library-card library-card--mystery" data-library-position="${index + 1}" aria-hidden="true">
        <span>?</span>
      </div>`;
  }

  function setSlot(card, offset, animate = true) {
    card.classList.remove(...slotClasses, 'is-hidden', 'is-settling');
    if (!animate) card.classList.add('is-settling');

    if (offset === -2) card.classList.add('is-far-left');
    else if (offset === -1) card.classList.add('is-left');
    else if (offset === 0) card.classList.add('is-center');
    else if (offset === 1) card.classList.add('is-right');
    else if (offset === 2) card.classList.add('is-far-right');
    else card.classList.add('is-hidden');
  }

  function create(element) {
    if (!element) return null;

    const query = new URLSearchParams(window.location.search);
    const queryCover = query.get('cover');
    const previewState = query.get('state');
    const coverUrl = element.dataset.cover || queryCover || '';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const carouselSequence = [...BASE_SEQUENCE, ...BASE_SEQUENCE, ...BASE_SEQUENCE];

    element.innerHTML = `
      <div class="carousel-stage">
        ${carouselSequence.map((type) => carouselCardMarkup(type, coverUrl)).join('')}
      </div>
      <div class="collection-cover-bridge" aria-hidden="true">
        <img src="${escapeAttribute(coverUrl)}" alt="" />
        <span class="status-check collection-cover-bridge__check">${checkIcon}</span>
      </div>
      <div class="library-stage" aria-hidden="true">
        <div class="library-grid">
          ${LIBRARY_SEQUENCE.map((type, index) => libraryCardMarkup(type, coverUrl, index)).join('')}
        </div>
      </div>`;

    const carouselCards = [...element.querySelectorAll('.carousel-card')];
    const pendingTimers = new Set();
    let activeIndex = BASE_SEQUENCE.length;
    let isVisible = true;
    let destroyed = false;

    const later = (callback, delay) => {
      const timer = window.setTimeout(() => {
        pendingTimers.delete(timer);
        if (!destroyed) callback();
      }, delay);
      pendingTimers.add(timer);
      return timer;
    };

    const stop = () => {
      pendingTimers.forEach((timer) => window.clearTimeout(timer));
      pendingTimers.clear();
    };

    const setMode = (mode) => {
      element.classList.remove(...modeClasses);
      if (mode !== 'carousel') element.classList.add(`is-${mode}`);
      element.dataset.mode = mode;
    };

    const applyPositions = (animate = true) => {
      carouselCards.forEach((card, index) => setSlot(card, index - activeIndex, animate));
      element.dataset.activeType = carouselCards[activeIndex].dataset.cardType;

      if (!animate) {
        void element.offsetWidth;
        carouselCards.forEach((card) => card.classList.remove('is-settling'));
      }
    };

    const showGridImmediately = () => {
      stop();
      activeIndex = BASE_SEQUENCE.length * 2;
      applyPositions(false);
      setMode('grid-view');
    };

    const runCycle = (resetPositions = true) => {
      stop();
      if (destroyed) return;

      if (resetPositions) {
        activeIndex = BASE_SEQUENCE.length;
        applyPositions(false);
      }

      setMode('carousel');

      if (previewState === 'grid') {
        showGridImmediately();
        return;
      }

      if (reducedMotion.matches || !isVisible || document.hidden) return;

      for (let step = 1; step <= CAROUSEL_STEPS; step += 1) {
        later(() => {
          activeIndex += 1;
          applyPositions(true);
        }, CAROUSEL_STEP_MS * step);
      }

      const finalCoverAt = CAROUSEL_STEP_MS * CAROUSEL_STEPS;
      const pressCoverAt = finalCoverAt + 620;
      const openGridAt = pressCoverAt + 180;
      const gridSettledAt = openGridAt + OPEN_GRID_MS;
      const pressGridAt = gridSettledAt + GRID_HOLD_MS;
      const closeGridAt = pressGridAt + 180;

      later(() => setMode('cover-press'), pressCoverAt);
      later(() => setMode('opening-grid'), openGridAt);
      later(() => {
        setMode('grid-view');
        activeIndex = BASE_SEQUENCE.length;
        applyPositions(false);
      }, gridSettledAt);
      later(() => setMode('grid-press'), pressGridAt);
      later(() => setMode('closing-grid'), closeGridAt);
      later(() => runCycle(false), closeGridAt + CLOSE_GRID_MS);
    };

    applyPositions(false);
    setMode('carousel');

    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible) runCycle();
      else stop();
    }, { threshold: 0.2 });

    const handleVisibilityChange = () => {
      if (document.hidden) stop();
      else runCycle();
    };
    const handleReducedMotionChange = () => runCycle();
    const handleRestart = () => runCycle();

    observer.observe(element);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotion.addEventListener('change', handleReducedMotionChange);
    element.addEventListener('carousel:restart', handleRestart);

    const controller = {
      element,
      start: runCycle,
      stop,
      showGrid: showGridImmediately,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        stop();
        observer.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        reducedMotion.removeEventListener('change', handleReducedMotionChange);
        element.removeEventListener('carousel:restart', handleRestart);
        const index = instances.indexOf(controller);
        if (index >= 0) instances.splice(index, 1);
      },
    };

    instances.push(controller);
    return controller;
  }

  window.VoidScreenCollectionCarousels = instances;
  window.VoidScreenCollectionCarousel = {
    version: 'seam-18-single-cover',
    create,
  };
  document.dispatchEvent(new CustomEvent(readyEventName));
})();
